/**
 * MinHeap - Priority Queue Implementation
 *
 * Optimized min-heap for O(log n) insertion and O(1) peek/O(log n) pop operations.
 * Used for efficiently retrieving miniblocks in sorted order.
 */

export class MinHeap<T> {
  private heap: T[] = [];
  private compareFn: (a: T, b: T) => number;

  /**
   * @param compareFn - Comparison function that returns:
   *   - negative if a < b
   *   - positive if a > b
   *   - zero if a === b
   */
  constructor(compareFn: (a: T, b: T) => number) {
    this.compareFn = compareFn;
  }

  /**
   * Get the minimum element without removing it
   * O(1) time complexity
   */
  public peek(): T | null {
    return this.heap.length > 0 ? this.heap[0] : null;
  }

  /**
   * Add an element to the heap
   * O(log n) time complexity
   */
  public push(value: T): void {
    this.heap.push(value);
    this.bubbleUp(this.heap.length - 1);
  }

  /**
   * Remove and return the minimum element
   * O(log n) time complexity
   */
  public pop(): T | null {
    if (this.heap.length === 0) return null;
    if (this.heap.length === 1) return this.heap.pop()!;

    const min = this.heap[0];
    this.heap[0] = this.heap.pop()!;
    this.bubbleDown(0);
    return min;
  }

  /**
   * Get the number of elements in the heap
   * O(1) time complexity
   */
  public get length(): number {
    return this.heap.length;
  }

  /**
   * Check if heap is empty
   * O(1) time complexity
   */
  public isEmpty(): boolean {
    return this.heap.length === 0;
  }

  /**
   * Clear all elements from the heap
   * O(1) time complexity
   */
  public clear(): void {
    this.heap = [];
  }

  /**
   * Bubble up element at index to maintain heap property
   */
  private bubbleUp(index: number): void {
    while (index > 0) {
      const parentIndex = Math.floor((index - 1) / 2);

      if (this.compareFn(this.heap[index], this.heap[parentIndex]) >= 0) {
        break; // Heap property satisfied
      }

      // Swap with parent
      [this.heap[index], this.heap[parentIndex]] = [this.heap[parentIndex], this.heap[index]];
      index = parentIndex;
    }
  }

  /**
   * Bubble down element at index to maintain heap property
   */
  private bubbleDown(index: number): void {
    const length = this.heap.length;

    while (true) {
      const leftChild = 2 * index + 1;
      const rightChild = 2 * index + 2;
      let smallest = index;

      if (leftChild < length && this.compareFn(this.heap[leftChild], this.heap[smallest]) < 0) {
        smallest = leftChild;
      }

      if (rightChild < length && this.compareFn(this.heap[rightChild], this.heap[smallest]) < 0) {
        smallest = rightChild;
      }

      if (smallest === index) {
        break; // Heap property satisfied
      }

      // Swap with smallest child
      [this.heap[index], this.heap[smallest]] = [this.heap[smallest], this.heap[index]];
      index = smallest;
    }
  }
}
